import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import ProfileControlField from "../../../shared/layouts/Header/UserProfile/ProfileControlField";
import { useGetUser } from "../../UserProfileSetting/hooks/userProfileHelper.hooks";
import { label, notifyIcon, quote } from "../../../config";
import { getNotification } from "../../../utils/common";
import { addTeamVariableValues, getTeamWorkInformation } from "../../Admin/projectConfiguration.service";
import {
  coercePayloadValue,
  sortWorkInformationsByOrder,
} from "./projectConfigurationsSection.helper";

/**
 * Project work-information controls for team settings: loads layout from the server and saves each change immediately.
 */
const ProjectConfigurationsSection = () => {
  const { projectId: routeProjectId } = useParams();
  const { projectDetails } = useSelector((state) => state.projectSidebar);
  const user = useGetUser();
  const { companyId } = user || {};

  const resolvedProjectId = routeProjectId ?? projectDetails?.ProjectId;
  const teamId = resolvedProjectId != null ? Number(resolvedProjectId) : null;

  const [profileControlsDataList] = useState({});
  const [workInformations, setWorkInformations] = useState([]);
  const [sortingOrderStr, setSortingOrderStr] = useState("");
  const [savingVariableId, setSavingVariableId] = useState(null);

  const sortedWorkInformations = useMemo(
    () => sortWorkInformationsByOrder(workInformations, sortingOrderStr),
    [workInformations, sortingOrderStr]
  );

  useEffect(() => {
    (async () => {
      if (!companyId || teamId == null || Number.isNaN(teamId)) {
        setWorkInformations([]);
        setSortingOrderStr("");
        return;
      }
      const workInformationData = await getTeamWorkInformation({
        companyId,
        projectId: teamId,
      });
      if (workInformationData) {
        setWorkInformations(Array.isArray(workInformationData.WorkInformations) ? workInformationData.WorkInformations : []);
        setSortingOrderStr((workInformationData.SortingOrder?.VariableSorting ?? ""));
      } else {
        setWorkInformations([]);
        getNotification(quote.SOMETHING_WENT_WRONG, notifyIcon.ERROR_ICON);
      }
    })();
  }, [companyId, teamId]);

  const persistValue = useCallback(
    async (variableItemId, rawValue, options = { showSaving: true }) => {
      if (!companyId || teamId == null || Number.isNaN(teamId) || !variableItemId) return;
      if (options.showSaving) setSavingVariableId(variableItemId);
      try {
        const payload = {
          values: [
            {
              teamId,
              companyId,
              variableItemId,
              value: rawValue,
            },
          ],
        };
        const result = await addTeamVariableValues(payload);
        if (result != null) {
          setWorkInformations((prev) =>
            prev.map((c) => (c._id === variableItemId ? { ...c, value: rawValue } : c))
          );
        } else {
          getNotification(quote.SOMETHING_WENT_WRONG, notifyIcon.ERROR_ICON);
        }
      } finally {
        if (options.showSaving) setSavingVariableId(null);
      }
    },
    [companyId, teamId]
  );

  const debouncedTextPersist = useMemo(
    () =>
      debounce((variableItemId, value) => {
        void persistValue(variableItemId, value, { showSaving: false });
      }, 1000),
    [persistValue]
  );

  useEffect(() => () => debouncedTextPersist.cancel(), [debouncedTextPersist]);

  const handleControlValueChange = useCallback(
    (event) => {
      let value = event?.value ?? event?.target?.value;
      if (value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "value")) {
        value = value.value;
      }
      const variableItemId = event?.target?.name;
      if (!variableItemId) return;

      const control = workInformations.find((c) => c._id === variableItemId);
      const isText = control && control?.variableType === "text";

      if (isText) {
        debouncedTextPersist(variableItemId, value);
        return;
      }
      persistValue(variableItemId, value, { showSaving: true });
    },
    [workInformations]
  );

  if (!sortedWorkInformations?.length) {
    return (
      <div className="profile-section-content bg-light">
        <p className="mb-0 text-muted small">{label.NO_PROJECT_CONFIGURATIONS_DEFINED}</p>
      </div>
    );
  }

  return (
    <div className="profile-section-content bg-light">
      <div className="mt-1">
        <div className="profile-control-fields-container">
          {sortedWorkInformations.map((control) => (
            <div key={control?._id} className={savingVariableId === control?._id ? "opacity-70" : undefined}>
              <ProfileControlField
                control={{
                  ...control,
                  isEditable: control?.isEditable !== false && savingVariableId !== control?._id,
                }}
                profileControlsDataList={profileControlsDataList}
                user={user}
                profileData={{}}
                handleChange={handleControlValueChange}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectConfigurationsSection;
